import { useId, useRef, useState } from 'react';
import { useDialog } from '../ui/useDialog';
import { X, CalendarClock, BellOff } from 'lucide-react';
import type { StatusSetPayload } from '../../types/consolidatedBusiness';
import { CB_STATUS_META } from './status';

interface Props {
    /** Which state the dialog is confirming. 'Open' and 'Lost' are set without a dialog. */
    target: 'Checked' | 'InProgress';
    /** Group label for the header (e.g. "PET Polyclear T 94"). */
    productName: string | null;
    /** Config default (D4) — pre-selects the matching preset for 'Checked'. */
    defaultReviewDays: number;
    /** Existing note, so re-opening "in progress" edits rather than starts over. */
    initialNote?: string | null;
    /**
     * Checked clears the In-progress note on the server (`runStatusSet`): shown here so the user
     * knows before confirming (design audit review, 2026-10-07).
     */
    noteToDrop?: string | null;
    busy?: boolean;
    onConfirm: (payload: StatusSetPayload) => void;
    onCancel: () => void;
}

const PRESETS = [30, 90, 180, 365];
const NOTE_MAX = 1000;

/**
 * Confirmation dialog for the two states that carry extra information.
 *
 *   Checked    — a review reminder is mandatory, exactly as before (D4).
 *   InProgress — BOTH the reminder and the note are optional (D3). A fifth pill,
 *                "No reminder", is the PRE-SELECTED choice, and the confirm button must stay
 *                enabled with a completely empty form. That is the common case: "I am on it,
 *                ask me nothing". Requiring a date would push people back to leaving the
 *                combination Open, which is the state this whole feature exists to split.
 */
export function ConsBusinessStatusModal({
    target, productName, defaultReviewDays, initialNote, noteToDrop, busy, onConfirm, onCancel,
}: Props) {
    const presetMatch = PRESETS.includes(defaultReviewDays) ? defaultReviewDays : 90;
    const inProgress = target === 'InProgress';

    // 'none' only exists for InProgress, where it is the default.
    const [mode, setMode] = useState<'none' | 'days' | 'date'>(inProgress ? 'none' : 'days');
    const [days, setDays] = useState<number>(presetMatch);
    const [date, setDate] = useState<string>('');
    const [note, setNote] = useState<string>(initialNote ?? '');

    const meta = CB_STATUS_META[target];
    const Icon = meta.icon;

    const confirm = () => {
        const payload: StatusSetPayload = { status: target };
        if (mode === 'date' && date) payload.reviewDueDate = date;
        else if (mode === 'days') payload.reviewDays = days;
        if (inProgress && note.trim()) payload.statusNote = note.trim();
        onConfirm(payload);
    };

    // Only an explicitly chosen but empty date can block confirming. An untouched form is valid
    // for InProgress by design.
    const blocked = busy || (mode === 'date' && !date);

    const panel = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const noteId = useId();
    useDialog(panel, onCancel);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={onCancel}>
            <div
                ref={panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="w-full max-w-md rounded-lg bg-white shadow-xl"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <h2 id={titleId} className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Icon aria-hidden="true" className={`h-4 w-4 ${meta.iconClass}`} />
                        {inProgress ? 'Mark as in progress' : 'Set review reminder'}
                    </h2>
                    <button type="button" onClick={onCancel} aria-label="Close" className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
                        <X aria-hidden="true" className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-4 px-4 py-4">
                    <p className="text-sm text-gray-600">
                        {inProgress ? 'Working on ' : 'Checking '}
                        <span className="font-semibold">{productName ?? 'this combination'}</span>.
                    </p>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">
                            {inProgress ? 'Remind me again in' : 'Review again in'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {inProgress && (
                                <button
                                    onClick={() => { setMode('none'); setDate(''); }}
                                    className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
                                        mode === 'none'
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <BellOff className="h-3.5 w-3.5" /> No reminder
                                </button>
                            )}
                            {PRESETS.map(p => (
                                <button
                                    key={p}
                                    onClick={() => { setMode('days'); setDays(p); setDate(''); }}
                                    className={`rounded-md border px-3 py-1.5 text-sm ${
                                        mode === 'days' && days === p
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    {p} days
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Or a specific date</label>
                        <div className="relative">
                            <CalendarClock className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="date"
                                aria-label="Review date"
                                value={date}
                                onChange={e => {
                                    setDate(e.target.value);
                                    setMode(e.target.value ? 'date' : (inProgress ? 'none' : 'days'));
                                }}
                                className={`w-full rounded-md border py-1.5 pl-8 pr-2 text-sm ${
                                    mode === 'date' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                                }`}
                            />
                        </div>
                    </div>

                    {!inProgress && noteToDrop && (
                        <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                            Checking removes the in-progress note: <span className="italic">“{noteToDrop}”</span>
                        </p>
                    )}

                    {inProgress && (
                        <div>
                            <label htmlFor={noteId} className="mb-1 block text-xs font-medium text-gray-600">
                                Note <span className="font-normal text-gray-500">(optional)</span>
                            </label>
                            <textarea
                                id={noteId}
                                value={note}
                                maxLength={NOTE_MAX}
                                rows={3}
                                onChange={e => setNote(e.target.value)}
                                placeholder="What is happening with this combination?"
                                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                            />
                            <p className="mt-0.5 text-right text-[11px] text-gray-500">
                                {note.length} / {NOTE_MAX}
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
                    <button
                        onClick={onCancel}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={confirm}
                        disabled={blocked}
                        className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {busy ? 'Saving…' : inProgress ? 'Mark in progress' : 'Check'}
                    </button>
                </div>
            </div>
        </div>
    );
}
